import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/api";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

export const AuthDialog = () => {
  const { authOpen, setAuthOpen, login, register } = useAuth();
  const [mode, setMode] = React.useState("login");
  const [name, setName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [loading, setLoading] = React.useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        await login(email, password);
        toast.success("Welcome back!");
      } else {
        await register(name, email, password);
        toast.success("Account created — enjoy your 30-day free trial!");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <Dialog open={authOpen} onOpenChange={setAuthOpen}>
      <DialogContent className="bg-sand-light border-border rounded-3xl sm:max-w-md" data-testid="auth-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-ink">
            {mode === "login" ? "Welcome back" : "Join Shabba Robo"}
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm text-ink/60 -mt-2">
          {mode === "login"
            ? "Sign in to unlock your premium features."
            : "Create a free account — get 30 days of premium free."}
        </p>

        <button
          onClick={googleLogin}
          className="w-full rounded-xl border border-border bg-white hover:bg-sand-dark/40 transition-colors py-3 flex items-center justify-center gap-3 font-semibold text-ink"
          data-testid="google-login-btn"
        >
          <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-5 h-5" />
          Continue with Google
        </button>

        <div className="flex items-center gap-3 text-xs text-ink/40">
          <span className="h-px flex-1 bg-border" /> or <span className="h-px flex-1 bg-border" />
        </div>

        <form onSubmit={submit} className="space-y-3" data-testid="auth-form">
          {mode === "register" && (
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name"
              required
              className="w-full rounded-xl bg-sand border border-border px-4 py-3 outline-none focus:ring-2 focus:ring-clay/30 focus:border-clay"
              data-testid="auth-name-input"
            />
          )}
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            required
            className="w-full rounded-xl bg-sand border border-border px-4 py-3 outline-none focus:ring-2 focus:ring-clay/30 focus:border-clay"
            data-testid="auth-email-input"
          />
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            required
            className="w-full rounded-xl bg-sand border border-border px-4 py-3 outline-none focus:ring-2 focus:ring-clay/30 focus:border-clay"
            data-testid="auth-password-input"
          />
          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-full bg-clay text-white font-bold py-3.5 hover:bg-clay-hover transition-transform hover:-translate-y-0.5 disabled:opacity-60 flex items-center justify-center gap-2"
            data-testid="auth-submit-btn"
          >
            {loading && <Loader2 size={18} className="animate-spin" />}
            {mode === "login" ? "Sign in" : "Create free account"}
          </button>
        </form>

        <p className="text-center text-sm text-ink/60">
          {mode === "login" ? "New here? " : "Already have an account? "}
          <button
            onClick={() => setMode(mode === "login" ? "register" : "login")}
            className="font-bold text-clay hover:underline"
            data-testid="auth-toggle-mode"
          >
            {mode === "login" ? "Create an account" : "Sign in"}
          </button>
        </p>
      </DialogContent>
    </Dialog>
  );
};
